import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Eye } from 'react-feather';
import { get, post } from '../services/ApiService';
import WindowBody from './Window/WindowBody';
import WindowFooter from './Window/WindowFooter';
import { UiButton } from './UiButton';
import Form from './Form/Form';
import CalculoQuedaTensaoForm from '../hooks/forms/CalculoQuedaTensaoForm';

const FORM_COMPONENTS: Record<string, React.ComponentType<any>> = {
    'CalculoQuedaTensaoForm': CalculoQuedaTensaoForm,
};

interface DocumentPreviewWindowProps {
    contratoId: number;
    documentUrl: string;
    formComponent: string;
    triggerClose: () => void;
}

export const DocumentPreviewWindow: React.FC<DocumentPreviewWindowProps> = ({
    contratoId,
    documentUrl,
    formComponent,
    triggerClose,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [iframeUrl, setIframeUrl] = useState<string>('');

    const methods = useForm();
    const { handleSubmit, reset } = methods;

    useEffect(() => {
        setIsLoading(true);
        const rows = encodeURIComponent(JSON.stringify([{ id: contratoId }]));
        get(`/?route=contrato/form&action=edit&rows=${rows}`)
            .then((response) => {
                if (response.data?.payload) {
                    reset(response.data.payload);
                }
            })
            .catch((error) => console.error('Erro ao carregar:', error))
            .finally(() => setIsLoading(false));
    }, [contratoId, reset]);

    const triggerSubmit = (data: any) => {
        setIsLoading(true);
        post(`/?route=contrato/form&action=put`, {
            dados: {
                id: contratoId,
                dados: { calculoPerda: data.dados?.calculoPerda },
            },
        })
            .then((response: any) => {
                if (response.status === 200 && response.data?.success) {
                    const url = `${process.env.REACT_APP_API_ENDPOINT}${documentUrl}?id=${contratoId}&t=${Date.now()}`;
                    setIframeUrl(url);
                } else {
                    alert('Erro ao salvar: ' + (response.data?.message || 'Erro desconhecido'));
                }
            })
            .catch(() => alert('Erro ao salvar dados'))
            .finally(() => setIsLoading(false));
    };

    const FormComponent = FORM_COMPONENTS[formComponent];

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(triggerSubmit)} methods={methods}>
                <WindowBody noPadding>
                    <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ width: '280px', flexShrink: 0, borderRight: '1px solid #dee2e6', overflowY: 'auto', padding: '20px' }}>
                            {FormComponent ? (
                                <FormComponent />
                            ) : (
                                <div className="alert alert-warning">
                                    Componente "{formComponent}" não encontrado.
                                </div>
                            )}
                        </div>
                        <div style={{ flex: 1, height: '100%', backgroundColor: '#f5f5f5', overflow: 'hidden' }}>
                            {iframeUrl ? (
                                <iframe
                                    src={iframeUrl}
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                    title="Preview do Documento"
                                />
                            ) : (
                                <div className="d-flex align-items-center justify-content-center h-100" style={{ color: '#999' }}>
                                    <div className="text-center">
                                        <Eye size={48} className="mb-3" />
                                        <p className="mb-0">Clique em "Salvar e Visualizar" para gerar o documento</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </WindowBody>
                <WindowFooter>
                    <UiButton variant="secondary" type="button" onClick={triggerClose}>Fechar</UiButton>
                    <UiButton variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? 'Salvando...' : 'Salvar e Visualizar'}
                    </UiButton>
                </WindowFooter>
            </Form>
        </FormProvider>
    );
};
